import { useRef } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { Spacing8 } from 'autogen/design-tokens/tokens';
import { mapTypeImageDetailedToImageProps } from 'components/generic/Image/helpers/mapToImageProps';
import { Body } from 'components/generic/typography';
import Shortcut from 'components/Shortcut';
import PageAction from 'components/shared/Routing/PageAction';
import useViewPromotionIntersectionObserver from 'modules/tracking/GA4/promotionEvents/hooks/useViewPromotionIntersectionObserver';
import trackSelectPromotion from 'modules/tracking/GA4/promotionEvents/trackSelectPromotion';
import type { TypeLinkDetailed, TypeTrackingPropertiesDetailed } from 'types/autogen/contentful';
import { breakpoint, sizes } from 'variables';
import { navigationLinkToPageAction } from '../../../shared/ContentfulHelpers';
import Image from '../../Image/Image';
import { widthFormulaLG, widthFormulaMD, widthFormulaSM, widthFormulaXS } from '../shared/styles';

// #region Styles
const Slide = styled.div`
    position: relative;
    flex: 1 0 100%;
    width: ${widthFormulaXS};
    display: flex;
    justify-content: center;

    ${breakpoint.up(sizes.sm)} {
        width: ${widthFormulaSM};
    }

    ${breakpoint.up(sizes.md)} {
        width: ${widthFormulaMD};
    }

    ${breakpoint.up(sizes.lg)} {
        width: ${widthFormulaLG};
    }

    ${breakpoint.up(sizes.xl)} {
        width: 112px;
    }

    ${breakpoint.up(sizes.xxl)} {
        width: 116px;
    }
`;

const ImageContainer = styled.div`
    height: ${widthFormulaXS};
    width: ${widthFormulaXS};
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    margin: 0 auto;

    ${breakpoint.up(sizes.sm)} {
        height: ${widthFormulaSM};
        width: ${widthFormulaSM};
    }

    ${breakpoint.up(sizes.md)} {
        height: ${widthFormulaMD};
        width: ${widthFormulaMD};
    }

    ${breakpoint.up(sizes.lg)} {
        height: ${widthFormulaLG};
        width: ${widthFormulaLG};
    }

    ${breakpoint.up(sizes.xl)} {
        height: 96px;
        width: 96px;
    }
`;

const StyledImage = styled(Image)`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ItemContent = styled.div`
    display: grid;
    grid-gap: ${Spacing8};
    align-items: center;
    width: 100%;
`;

const Text = styled(Body)`
    text-align: center;
    margin-bottom: 0;
    width: 100%;
`;

// #endregion Styles

function Content({
    imageRatio11,
    headline,
    ...restProps
}: Pick<TypeLinkDetailed['fields'], 'imageRatio11' | 'headline'>) {
    return (
        <ItemContent {...restProps}>
            <ImageContainer>{imageRatio11 && <StyledImage {...imageRatio11} sizes="100px" />}</ImageContainer>
            <Text size="M">{headline}</Text>
        </ItemContent>
    );
}

function Item({
    link,
    trackingProperties,
    ...restProps
}: {
    link: TypeLinkDetailed;
    trackingProperties?: TypeTrackingPropertiesDetailed['fields'];
}) {
    if (!link) return;

    const {
        fields: { headline, imageRatio11 },
        sys: { id }
    } = link;

    const linkAction = navigationLinkToPageAction(link);
    const inspectorProps = useContentfulInspectorMode({ entryId: id });
    const trackView = useViewPromotionIntersectionObserver();
    const ref = useRef<HTMLAnchorElement>(null);
    const imageProps = imageRatio11 && mapTypeImageDetailedToImageProps(imageRatio11);

    if (!linkAction || !imageProps || !headline) return null;

    const baseTrackingData = trackingProperties && {
        creative_name: trackingProperties.creative_name,
        creative_slot: trackingProperties.creative_slot || 'Brand slider',
        promotion_id: trackingProperties.promotion_id,
        promotion_name: trackingProperties.promotion_name,
        items: [{ item_name: headline || 'None' }]
    };

    // view tracking
    if (baseTrackingData) {
        trackView({
            elementRef: ref,
            trackingType: 'EnhancedEcommerceTrackingEvent',
            ecommerce: {
                $type: 'EcommercePromotionImpression',
                ...baseTrackingData
            }
        });
    }

    // click tracking
    function handleClickTracking() {
        if (baseTrackingData && ref.current) {
            trackSelectPromotion({
                ecommerce: { $type: 'EcommercePromotionClick', ...baseTrackingData },
                trackingType: 'EnhancedEcommerceTrackingEvent'
            });
        }
    }

    return (
        <Slide {...restProps}>
            <Shortcut
                action={linkAction}
                imageProps={imageProps}
                label={headline}
                ref={ref}
                onClick={handleClickTracking}
                {...inspectorProps({ fieldId: 'internalName' })}
            />
        </Slide>
    );
}

export default withErrorBoundary(Item, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
